import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { RentablePhotoActions, RentablePhotosField, useStagedPhotos } from '../../Components/RentableDetail/RentablePhotosField';
import { Unit, UnitCreateInput, UnitInput } from '../../types/units';
import { Stairwell } from '../../types/stairwells';
import { STAIRWELL_NAME_MAX_LENGTH } from '../../Helpers/stairwellUtils';
import { UnitKind, UnitTenure } from '../../types/Enums';
import { translate } from '../../types/EnumTranslations';
import { LevelModel, RoomModel } from '../../types/protocols';
import { RoomImpl } from '../../types/implementations';
import { naturalCompare } from '../../Helpers/naturalSort';
import { CustomerType } from '../../types/customerType';
import { levelDisplayName } from '../../Helpers/levelDisplayName';
import {
    BOSTADSRATT_RESPONSIBILITY_NOTE,
    defaultTenure,
    duplicateUnitNumber,
    selectableTenures,
    unitDisplayName,
    unitHoldingRoom,
    unitLevelsDescending,
    unitRoomKeys,
    unitTypeIcon,
} from '../../Helpers/unitUtils';

/** Picker value for the inline create; never a push key. */
const NEW_STAIRWELL = '__new';

interface UnitModalProps {
    show: boolean;
    onHide: () => void;
    onSubmit: (input: UnitCreateInput) => Promise<void>;
    levels: { [key: string]: LevelModel };
    /** The building's rooms, for the room picker (plan D6). */
    rooms?: { [key: string]: RoomModel };
    /**
     * Create only: the level whose drawing was clicked. With no rooms picked
     * the whole-unit room is created there at the click; without a click the
     * unit is created unplaced.
     */
    placementLevelKey?: string;
    /** Every unit in the building, for the duplicate-number warning and room exclusivity. */
    allUnits: Unit[];
    /** When given, the modal edits this unit; otherwise it creates one. */
    unit?: Unit;
    /** Pre-filled values for a new unit (series placement). */
    initialValues?: Partial<UnitInput>;

    /** The building's stairwells in natural order, for the Trappuppgång picker. */
    stairwells: Stairwell[];
    /** "Ny trappuppgång…": creates it at once (validated by the API) and returns it to be picked. */
    onCreateStairwell: (name: string) => Promise<Stairwell>;
    /** Instance customer type; drives tenure options and defaults (plan D4). */
    customerType: CustomerType | null;
    submitting?: boolean;
    /** Editing: the Foton field; changes apply after the object is saved, so Avbryt changes nothing. */
    photoActions?: RentablePhotoActions;
}

/**
 * Create/edit form for a unit (lägenhet or lokal). Kind switches which
 * descriptive fields are shown; the other kind's fields are dropped on save
 * (plan D11). A unit is its rooms: on create from a drawing click, picking
 * none makes a whole-unit room at the click; otherwise no rooms means the
 * unit is unplaced until rooms are linked.
 */
const UnitModal: React.FC<UnitModalProps> = ({
    show, onHide, onSubmit, levels, rooms = {}, placementLevelKey, allUnits, unit, initialValues, stairwells, onCreateStairwell, customerType, submitting = false, photoActions
}) => {
    const stagedPhotos = useStagedPhotos('unit');
    const { reset: resetPhotos } = stagedPhotos;
    useEffect(() => {
        if (show) resetPhotos();
    }, [show, resetPhotos]);
    const busy = submitting || stagedPhotos.busy;
    const isEditing = !!unit;
    const [kind, setKind] = useState<UnitKind>(UnitKind.Apartment);
    const [tenure, setTenure] = useState<UnitTenure>(UnitTenure.Hyresratt);
    const [number, setNumber] = useState('');
    const [officialNumber, setOfficialNumber] = useState('');
    const [roomCountText, setRoomCountText] = useState('');
    const [commercialType, setCommercialType] = useState('');
    const [areaText, setAreaText] = useState('');
    const [roomKeys, setRoomKeys] = useState<string[]>([]);
    // The room picker is collapsed on create until opened; a new unit gets a whole-unit room by default (plan D13).
    const [showRoomPicker, setShowRoomPicker] = useState(false);
    const [comment, setComment] = useState('');
    const [stairwellKey, setStairwellKey] = useState('');
    // Inline "Ny trappuppgång…": the name being typed, or null while the picker shows.
    const [newStairwellName, setNewStairwellName] = useState<string | null>(null);
    const [creatingStairwell, setCreatingStairwell] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const sortedLevels = useMemo(() => unitLevelsDescending(levels), [levels]);
    const tenureOptions = useMemo(() => {
        const options = selectableTenures(customerType);
        // Never hide the value an existing unit already has.
        return unit && !options.includes(unit.tenure) ? [unit.tenure, ...options] : options;
    }, [customerType, unit]);

    useEffect(() => {
        if (!show) return;
        const source: Partial<UnitInput> = unit ?? (initialValues || {});
        const nextKind = source.kind || UnitKind.Apartment;
        setKind(nextKind);
        setTenure(source.tenure || defaultTenure(customerType, nextKind));
        setNumber(source.number || '');
        setOfficialNumber(source.officialNumber || '');
        setRoomCountText(source.roomCount !== undefined ? String(source.roomCount) : '');
        setCommercialType(source.commercialType || '');
        setAreaText(source.area !== undefined ? String(source.area).replace('.', ',') : '');
        const initialRoomKeys = unit ? unitRoomKeys(unit) : [];
        setRoomKeys(initialRoomKeys);
        setShowRoomPicker(!!unit);
        setComment(source.comment || '');
        setStairwellKey(source.stairwellKey || '');
        setNewStairwellName(null);
        setError(null);
    }, [show, unit, initialValues, sortedLevels, customerType]);

    const handleKindChange = (nextKind: UnitKind) => {
        setKind(nextKind);
        // A new unit follows the instance default; an existing unit keeps its tenure.
        if (!isEditing) setTenure(defaultTenure(customerType, nextKind));
    };

    const toggleRoom = (roomKey: string, checked: boolean) => {
        setRoomKeys(prev => (checked ? [...prev, roomKey] : prev.filter(k => k !== roomKey)));
    };

    const placementLevel = placementLevelKey ? levels[placementLevelKey] : undefined;

    // Every room in the building, grouped per level (highest first), natural order by number.
    const roomGroups = useMemo(() => sortedLevels
        .map(l => ({
            level: l,
            rooms: Object.values(rooms)
                .filter(room => room && room.levelKey === l.key)
                .sort((a, b) => naturalCompare(a.number || a.label || '', b.number || b.label || '') || a.key.localeCompare(b.key)),
        }))
        .filter(group => group.rooms.length > 0), [sortedLevels, rooms]);

    const duplicate = useMemo(
        () => duplicateUnitNumber(allUnits, kind, number, unit?.key),
        [allUnits, kind, number, unit?.key]
    );
    const isApartment = kind === UnitKind.Apartment;
    // A dangling key (stairwell deleted elsewhere) reads as none.
    const pickedStairwellKey = stairwells.some(s => s.key === stairwellKey) ? stairwellKey : '';

    const handleStairwellSelect = (value: string) => {
        if (value === NEW_STAIRWELL) {
            setNewStairwellName('');
        } else {
            setStairwellKey(value);
        }
    };

    const createStairwell = async () => {
        if (newStairwellName === null) return;
        setError(null);
        setCreatingStairwell(true);
        try {
            const created = await onCreateStairwell(newStairwellName);
            setStairwellKey(created.key);
            setNewStairwellName(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte skapa trappuppgången');
        } finally {
            setCreatingStairwell(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!number.trim()) {
            setError(isApartment ? 'Lägenhetsnummer krävs' : 'Lokalnummer krävs');
            return;
        }
        let area: number | undefined;
        if (areaText.trim()) {
            const parsed = parseFloat(areaText.replace(',', '.'));
            if (!Number.isFinite(parsed) || parsed <= 0) {
                setError('Yta måste vara ett positivt antal kvadratmeter');
                return;
            }
            area = Math.round(parsed * 10) / 10;
        }
        let roomCount: number | undefined;
        if (isApartment && roomCountText.trim()) {
            const parsed = parseInt(roomCountText, 10);
            if (!Number.isInteger(parsed) || parsed <= 0) {
                setError('Antal rum måste vara ett positivt heltal');
                return;
            }
            roomCount = parsed;
        }

        try {
            await onSubmit({
                kind,
                tenure,
                number: number.trim(),
                officialNumber: isApartment ? (officialNumber.trim() || undefined) : undefined,
                area,
                roomCount,
                commercialType: !isApartment ? (commercialType.trim() || undefined) : undefined,
                roomKeys,
                comment: comment.trim() || undefined,
                stairwellKey: pickedStairwellKey || undefined,
            });
            if (isEditing && photoActions) await stagedPhotos.apply(photoActions);
            onHide();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte spara enheten');
        }
    };

    return (
        <Modal show={show} onHide={busy ? () => undefined : onHide} centered>
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? `Redigera ${isApartment ? 'lägenhet' : 'lokal'}` : 'Ny lägenhet eller lokal'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

                    <Form.Group className="mb-3">
                        <Form.Label>Typ *</Form.Label>
                        <div className="d-flex gap-3">
                            {Object.values(UnitKind).map(value => (
                                <Form.Check
                                    key={value}
                                    type="radio"
                                    id={`unit-kind-${value}`}
                                    name="kind"
                                    label={(
                                        <span className="d-inline-flex align-items-center gap-1">
                                            <img src={unitTypeIcon(value)} alt="" aria-hidden="true" width={20} height={20} />
                                            {translate(value)}
                                        </span>
                                    )}
                                    checked={kind === value}
                                    onChange={() => handleKindChange(value)}
                                />
                            ))}
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Upplåtelseform *</Form.Label>
                        <Form.Select value={tenure} onChange={e => setTenure(e.target.value as UnitTenure)}>
                            {tenureOptions.map(value => (
                                <option key={value} value={value}>{translate(value)}</option>
                            ))}
                        </Form.Select>
                        {tenure === UnitTenure.Bostadsratt && (
                            <Form.Text muted>{BOSTADSRATT_RESPONSIBILITY_NOTE}. Bostadsrätter får inga hyresavtal här.</Form.Text>
                        )}
                        {tenure === UnitTenure.Own && (
                            <Form.Text muted>Används av föreningen eller ägaren själv och hyrs inte ut.</Form.Text>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{isApartment ? 'Lägenhetsnummer' : 'Lokalnummer'} *</Form.Label>
                        <Form.Control
                            type="text"
                            value={number}
                            onChange={e => setNumber(e.target.value.toUpperCase())}
                            placeholder={isApartment ? 'T.ex. 1203 eller 12' : 'T.ex. 3 eller B1'}
                            autoFocus
                        />
                        {duplicate && (
                            <Form.Text className="text-warning d-block">Numret används redan av en annan {isApartment ? 'lägenhet' : 'lokal'} i byggnaden.</Form.Text>
                        )}
                    </Form.Group>

                    {isApartment ? (
                        <div className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Lgh-nr (Lantmäteriet)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        inputMode="numeric"
                                        value={officialNumber}
                                        onChange={e => setOfficialNumber(e.target.value)}
                                        placeholder="T.ex. 1101"
                                    />
                                    <Form.Text muted>Fyra siffror, valfritt.</Form.Text>
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Antal rum (rok)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min={1}
                                        step={1}
                                        value={roomCountText}
                                        onChange={e => setRoomCountText(e.target.value)}
                                        placeholder="T.ex. 3"
                                    />
                                </Form.Group>
                            </div>
                        </div>
                    ) : (
                        <Form.Group className="mb-3">
                            <Form.Label>Typ av lokal</Form.Label>
                            <Form.Control
                                type="text"
                                value={commercialType}
                                onChange={e => setCommercialType(e.target.value)}
                                placeholder="T.ex. Kontor, Butik, Frisör"
                                maxLength={100}
                            />
                        </Form.Group>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="unit-stairwell">Trappuppgång</Form.Label>
                        {newStairwellName === null ? (
                            <Form.Select id="unit-stairwell" value={pickedStairwellKey} onChange={e => handleStairwellSelect(e.target.value)}>
                                <option value="">Ingen</option>
                                {stairwells.map(stairwell => (
                                    <option key={stairwell.key} value={stairwell.key}>{stairwell.name}</option>
                                ))}
                                <option value={NEW_STAIRWELL}>Ny trappuppgång…</option>
                            </Form.Select>
                        ) : (
                            <div className="d-flex gap-2">
                                <Form.Control
                                    id="unit-stairwell"
                                    type="text"
                                    value={newStairwellName}
                                    onChange={e => setNewStairwellName(e.target.value)}
                                    onKeyDown={e => {
                                        // Enter adds the stairwell instead of saving the whole unit.
                                        if (e.key === 'Enter') { e.preventDefault(); void createStairwell(); }
                                    }}
                                    placeholder="T.ex. A eller Storgatan 3A"
                                    maxLength={STAIRWELL_NAME_MAX_LENGTH}
                                    autoFocus
                                />
                                <Button variant="outline-primary" onClick={() => void createStairwell()} disabled={creatingStairwell || !newStairwellName.trim()}>
                                    {creatingStairwell ? <Spinner size="sm" animation="border" /> : 'Lägg till'}
                                </Button>
                                <Button variant="link" onClick={() => setNewStairwellName(null)} disabled={creatingStairwell}>Avbryt</Button>
                            </div>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Rum{roomKeys.length > 0 ? ` (${roomKeys.length})` : ''}</Form.Label>
                        {!showRoomPicker ? (
                            <div>
                                <div style={{ fontSize: '14px' }}>
                                    {placementLevel
                                        ? `Ett rum "${number.trim() || (isApartment ? 'Lägenhet' : 'Lokal')}" skapas där du klickade på ${levelDisplayName(placementLevel)}.`
                                        : 'Enheten skapas utan rum och visas under Utan rum tills du kopplar rum.'}
                                </div>
                                <Button variant="link" size="sm" className="p-0" onClick={() => setShowRoomPicker(true)}>Koppla befintliga rum{placementLevel ? ' i stället' : ''}</Button>
                                <Form.Text muted className="d-block">Enheten ligger på sina rums våningar och visas mitt bland dem på ritningen.</Form.Text>
                            </div>
                        ) : roomGroups.length === 0 ? (
                            <div className="text-muted" style={{ fontSize: '14px' }}>Inga rum registrerade i byggnaden.</div>
                        ) : (
                            <div className="units-room-groups">
                                {roomGroups.map(group => (
                                    <div key={group.level.key} className="units-room-group">
                                        <div className="units-room-group-title">{levelDisplayName(group.level)}</div>
                                        {group.rooms.map(room => {
                                            const holder = unitHoldingRoom(allUnits, room.key, unit?.key);
                                            return (
                                                <Form.Check
                                                    key={room.key}
                                                    type="checkbox"
                                                    id={`unit-room-${room.key}`}
                                                    label={holder ? `${new RoomImpl(room).displayName()} · tillhör ${unitDisplayName(holder as Unit)}` : new RoomImpl(room).displayName()}
                                                    checked={roomKeys.includes(room.key)}
                                                    disabled={!!holder}
                                                    onChange={e => toggleRoom(room.key, e.target.checked)}
                                                />
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        )}
                        {showRoomPicker && (
                            <Form.Text muted>
                                Ett rum kan bara tillhöra en enhet. Enheten ligger på rummens våningar, och komponenterna i rummen visas på enheten.
                                {roomKeys.length === 0 && (placementLevel
                                    ? ` Utan valda rum skapas ett rum där du klickade på ${levelDisplayName(placementLevel)}.`
                                    : ' Utan rum visas enheten under Utan rum och på ingen ritning.')}
                            </Form.Text>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Yta ({isApartment ? 'BOA' : 'LOA'}, m²)</Form.Label>
                        <Form.Control
                            type="text"
                            inputMode="decimal"
                            value={areaText}
                            onChange={e => setAreaText(e.target.value)}
                            placeholder="T.ex. 72,5"
                        />
                    </Form.Group>

                    {isEditing && photoActions && <RentablePhotosField actions={photoActions} staged={stagedPhotos} />}

                    <Form.Group className="mb-1">
                        <Form.Label>Kommentar</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={comment}
                            onChange={e => setComment(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={busy}>Avbryt</Button>
                    <Button type="submit" variant="primary" disabled={busy || creatingStairwell}>
                        {busy ? <><Spinner size="sm" animation="border" /> Sparar...</> : 'Spara'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default UnitModal;
